/**
 * Org Detail / Streaming tab (Task 32c.2).
 *
 * Consumes the 32b proxy endpoint GET /api/platform/orgs/:id/servers.
 * The upstream server-team endpoint isn't built yet, so the proxy returns
 * an empty array today (prodApiClient.getOrgServers stub) — the tab will
 * render the standard "No streaming servers" empty state until that lands.
 *
 * Same 503 SERVER_UNAVAILABLE handling pattern as Projects/Users tabs.
 */
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { EmptyState, ErrorState, LoadingSkeleton } from '@repro/ui';
import { api } from '../../../utils/api';
import type { ServerStreamingServer } from '../../../types/platform';
import type { OrgDetailContext } from '../OrgDetail';

export default function Streaming() {
  const { organisation } = useOutletContext<OrgDetailContext>();
  const [servers, setServers] = useState<ServerStreamingServer[] | null>(null);
  const [serverUnavailable, setServerUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setServerUnavailable(false);
    api
      .getPlatformOrgServers(organisation.id)
      .then((data) => {
        if (cancelled) return;
        if (data === null) {
          setServerUnavailable(true);
          setServers([]);
        } else {
          setServers(data);
        }
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load streaming servers');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organisation.id]);

  if (loading) {
    return (
      <div className="mt-4">
        <LoadingSkeleton rows={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4">
        <ErrorState title="Couldn't load streaming servers" body={error} />
      </div>
    );
  }

  if (serverUnavailable) {
    return (
      <div className="mt-4">
        <EmptyState
          title="Production server unavailable"
          body="The upstream RePro server didn't respond. Try again in a moment."
        />
      </div>
    );
  }

  if (!servers || servers.length === 0) {
    return (
      <div className="mt-4">
        <EmptyState
          title="No streaming servers assigned"
          body="This organisation has no streaming servers in the production inventory yet."
        />
      </div>
    );
  }

  return (
    <div className="mt-4 bg-base-100 shadow rounded-lg overflow-hidden">
      <table className="table w-full">
        <thead>
          <tr>
            <th>Name</th>
            <th>Region</th>
            <th>Status</th>
            <th className="text-right">Load</th>
            <th className="text-right">Capacity</th>
          </tr>
        </thead>
        <tbody>
          {servers.map((server) => (
            <tr key={server.id}>
              <td>
                <div className="font-medium text-base-content">{server.name}</div>
              </td>
              <td className="text-sm text-base-content/80">{server.region ?? '—'}</td>
              <td>
                <StatusBadge status={server.status} />
              </td>
              <td className="text-right text-sm tabular-nums">
                {formatLoad(server.current_load, server.capacity)}
              </td>
              <td className="text-right text-sm tabular-nums text-base-content/70">
                {server.capacity ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }: { status: ServerStreamingServer['status'] }) {
  const map: Record<NonNullable<ServerStreamingServer['status']>, { cls: string; dot: string }> = {
    online: { cls: 'bg-green-100 text-green-800', dot: 'bg-green-500' },
    degraded: { cls: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500' },
    offline: { cls: 'bg-red-100 text-red-800', dot: 'bg-red-500' },
  };
  if (!status) {
    return <span className="text-base-content/50 text-xs">—</span>;
  }
  const { cls, dot } = map[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {status}
    </span>
  );
}

function formatLoad(load: number | undefined, capacity: number | undefined): string {
  if (load === undefined || load === null) return '—';
  if (capacity && capacity > 0) {
    const pct = Math.round((load / capacity) * 100);
    return `${load} (${pct}%)`;
  }
  return String(load);
}
